import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthApp } from "./AuthApp";
import { AuthFlowController } from "./auth-flow";
import "./auth.css";
import "./app-navigation.css";
import { FirebaseAuthGateway } from "./firebase-auth-gateway";
import { FirebasePortfolioApiClient } from "./portfolio-overview-api";
import { AdminUsersApi } from "./admin-users-api";
import {
  applyLocaleToDocument,
  LocaleProvider,
  resolveInitialLocale,
} from "./localization";
import { applyThemeToDocument, resolveInitialTheme } from "./theme";
import { installPhase12SyntheticViewerHook } from "./phase12-synthetic-viewer-hook.ts";
import { optionalPhase12SourceDigest } from "./phase12-source-marker.ts";
import { CENTRAL_FIREBASE_CONFIG } from "./firebase-config";
import { ACCOUNT_ACTION_PATH } from "./account-action";
import { createPasswordActionPage } from "./PasswordActionPage";

function createDashboard() {
  const authGateway = new FirebaseAuthGateway();
  const authController = new AuthFlowController(authGateway);
  installPhase12SyntheticViewerHook(
    authController,
    authGateway,
    CENTRAL_FIREBASE_CONFIG.projectId ?? "",
    optionalPhase12SourceDigest(import.meta.env.VITE_SOURCE_DIGEST),
    CENTRAL_FIREBASE_CONFIG,
  );
  const overviewClient = new FirebasePortfolioApiClient(
    (forceRefresh) => authGateway.getAccessToken(forceRefresh),
    import.meta.env.VITE_API_BASE_URL ?? "",
  );
  const adminUsersClient = new AdminUsersApi({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? "",
    getIdToken: (forceRefresh) => authGateway.getAccessToken(forceRefresh),
  });
  return (
    <AuthApp
      controller={authController}
      overviewClient={overviewClient}
      adminUsersClient={adminUsersClient}
    />
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing application root");

const isAccountAction = window.location.pathname === ACCOUNT_ACTION_PATH;
const initialLocale = isAccountAction ? "ar-SA" : resolveInitialLocale();
applyLocaleToDocument(initialLocale);
applyThemeToDocument(isAccountAction ? "light" : resolveInitialTheme());
const page = isAccountAction
  ? createPasswordActionPage(window.location.search)
  : createDashboard();
createRoot(root).render(
  <StrictMode>
    <LocaleProvider initialLocale={initialLocale}>{page}</LocaleProvider>
  </StrictMode>,
);
